import { addBreadcrumb } from "@sentry/react";
import { useAuth } from "@xemplo/auth-provider";
import { useGetNavigationQuery } from "@xemplo/directory-services-query";
import { UserPersona } from "@xemplo/gp-types";
import { safeCallAsync } from "@xemplo/safe-call";
import {
  PersonaExtended,
  XemploSessionData,
  XemploStateScopes,
} from "@xemplo/types";
import { getHrModuleUrl } from "@xemplo/common-util";
import { usePatchXemploStateMutation } from "@xemplo/xemplo-state-query";

/**
 * Given a GP persona, finds the corresponding Directory Services persona by
 * matching `directoryServicePersonaId`.
 */
function findMatchingDSPersona(
  persona: UserPersona,
  personaLookup?: Record<string, PersonaExtended>,
): PersonaExtended | undefined {
  const { directoryServicePersonaId } = persona;
  if (directoryServicePersonaId && personaLookup) {
    return personaLookup[directoryServicePersonaId];
  }
}

/**
 * Patches the Directory Services session-state record (`api/v2/state/session`)
 * to match a GP persona switch, so other apps see the correct persona on
 * their next bootstrap. This mirrors what the shared persona-panel dropdown
 * (`@xemplo/user-options`, used by `PrimaryNav`) already does before it
 * switches persona — but that component isn't the only persona-switch entry
 * point in this app (e.g. the post-login "select your profile" screen), so
 * those need to call this explicitly.
 */
export function usePersonaSessionSync() {
  const { user } = useAuth();
  const { data } = useGetNavigationQuery({ authToken: user?.access_token });
  const { mutateAsync } =
    usePatchXemploStateMutation<Partial<XemploSessionData>>();

  const syncSessionState = async (persona: UserPersona) => {
    const dsPersona = findMatchingDSPersona(persona, data?.personaLookup);
    if (!dsPersona) return;

    const sessionData = {
      personaId: dsPersona.id,
      companyId: dsPersona.companyId?.toString(),
      instanceUrl: getHrModuleUrl(dsPersona.instance),
      origin: window.location.origin,
    };

    const [, error] = await safeCallAsync(
      mutateAsync({ data: sessionData, scope: XemploStateScopes.Session }),
    );
    if (error) {
      addBreadcrumb({
        category: "auth",
        message: "Failed to sync persona to session state",
        level: "error",
        data: {
          personaId: sessionData.personaId,
          error: error instanceof Error ? error.message : String(error),
        },
      });
    }
  };

  return { syncSessionState };
}
